import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { type ReactNode } from 'react';
import {
  Card,
  StatCard,
  EyebrowLabel,
  Badge,
  Alert,
  TabBar,
  SkeletonText,
  ExpandIcon,
  CalendarIcon,
  TimeIcon,
} from '@thegpvc/ui';
import { Monitor, Cpu, GitPullRequest } from 'lucide-react';
import { api } from '../api/client';
import { CopyButton } from '../components/CopyButton';
import { text, bg, pageTitle, colWidth, cx } from '../styles/theme';
import { TimeRibbon } from '../components/TimeRibbon';
import { PageContainer } from '../components/PageContainer';
import { SectionCard } from '../components/SectionCard';
import { CoachingInsights } from '../components/CoachingInsights';
import { CoachingRollup } from '../components/CoachingRollup';
import { SessionFriction } from '../components/SessionFriction';
import { SessionPulseCard } from '../components/SessionPulseCard';
import { SessionChapters } from '../components/SessionChapters';
import { SourceBadge } from '../components/SourceBadge';
import { formatClockTime, formatDate, formatDuration, formatInt, formatPercentage, formatUsd } from '../utils/format';
import { errorMessage } from '../utils/errorMessage';
import { TOTAL_TOKENS_TOOLTIP, COST_TOKENS_TOOLTIP, sumCostTokens } from '../utils/tokenLabels';
import type { SessionDetail as SessionDetailType, SessionSummaryResponse, RecentActivityMessage } from '../types/api';

/** Explains the otherwise-opaque autonomy percentage shown in the Metrics grid. */
const AUTONOMY_TOOLTIP =
  'Autonomy = AI turns ÷ (user + AI turns) for this session. Higher means more of the work ran AI-driven between your prompts.';

/** Explains the stretch distribution — the #362 replacement for the saturating autonomy score. */
const STRETCH_TOOLTIP =
  'A "stretch" is a run of consecutive tool calls between your prompts. Each stretch length is measured in tool calls, and Max Stretch is the longest such run. Longer stretches mean more autonomous work ran between your messages.';

/**
 * Shown for Cache Creation when this session's model never writes prompt cache
 * (#743). Some providers — GLM via LunaRoute — read cache but report zero
 * creation on every turn. Rendering that literal 0 reads as "measured, and it
 * is fine" when the honest answer is "this metric does not apply here".
 */
const NO_CACHE_WRITE_TOOLTIP =
  'This session’s model reads prompt cache but never reports writing any, so cache-creation metrics do not apply.';

/** Human labels for the session_mode classification (empty string = unclassified → badge hidden). */
const SESSION_MODE_LABELS: Record<string, { label: string; variant: 'info' | 'neutral' }> = {
  'long-autonomy': { label: 'Long autonomy', variant: 'info' },
  'choppy-interactive': { label: 'Choppy / interactive', variant: 'neutral' },
};

/**
 * Event log — one row per activity event: a fixed-width timestamp column on the
 * left, a small role tag, then the event text. Rows are divided by a thin rule
 * (per the Session Story prototype). Sourced from the summary's timestamped
 * recent-activity stream.
 */
function EventLog({ events }: { events: RecentActivityMessage[] }) {
  if (events.length === 0) return null;
  return (
    <ul className="divide-y divide-gray-100 dark:divide-navy-700">
      {events.map((e, idx) => (
        <li key={idx} className="flex items-start gap-3 py-2">
          <span className={cx('shrink-0 pt-0.5 font-mono text-[11px] tabular-nums', colWidth.valueMd, text.muted)}>
            {formatClockTime(e.timestamp)}
          </span>
          <Badge variant={e.role === 'user' ? 'warning' : 'neutral'} size="sm">{e.role}</Badge>
          <span className={cx('min-w-0 text-[13px] leading-relaxed break-words', text.secondary)}>{e.content}</span>
        </li>
      ))}
    </ul>
  );
}

/** Combined session + subagent token totals, computed once. */
function tokenTotals(session: SessionDetailType) {
  const input = parseInt(session.inputTokens || '0', 10) + parseInt(session.subagentInputTokens || '0', 10);
  const output = parseInt(session.outputTokens || '0', 10) + parseInt(session.subagentOutputTokens || '0', 10);
  const cacheCreation = parseInt(session.cacheCreationTokens || '0', 10) + parseInt(session.subagentCacheCreationTokens || '0', 10);
  const cacheRead = parseInt(session.cacheReadTokens || '0', 10) + parseInt(session.subagentCacheReadTokens || '0', 10);
  const total = input + output + cacheCreation + cacheRead;
  // Cost-weighted tokens — weights live in COST_TOKEN_WEIGHTS. Summed per source
  // (session + subagents) since the weighting is linear.
  const cost =
    sumCostTokens({
      inputTokens: session.inputTokens,
      outputTokens: session.outputTokens,
      cacheCreationTokens: session.cacheCreationTokens,
      cacheReadTokens: session.cacheReadTokens,
    }) +
    sumCostTokens({
      inputTokens: session.subagentInputTokens,
      outputTokens: session.subagentOutputTokens,
      cacheCreationTokens: session.subagentCacheCreationTokens,
      cacheReadTokens: session.subagentCacheReadTokens,
    });
  const cacheHitRatio = total > 0 ? cacheRead / total : 0;
  // A model that reads cache but reports zero creation does not write prompt
  // cache at all (#743) — distinct from a genuinely cache-free short session
  // (both counters 0). Surface "N/A" for cache-creation rather than a
  // deceptive 0 that reads as "measured, and fine".
  const noCacheWrites = cacheCreation === 0 && cacheRead > 0;
  return { input, output, cacheCreation, cacheRead, total, cost, cacheHitRatio, noCacheWrites };
}

/** A collapsed expander used inside the Details view. */
function Disclosure({ title, open = false, children }: { title: ReactNode; open?: boolean; children: ReactNode }) {
  return (
    <details open={open} className="group">
      <summary className={cx('cursor-pointer select-none flex items-center gap-2 text-sm font-medium', text.secondary)}>
        <ExpandIcon className="w-4 h-4 transition-transform group-open:rotate-90" />
        {title}
      </summary>
      <div className="mt-3">{children}</div>
    </details>
  );
}

/** The AI roll-up (thesis) + PR outcomes — top of the Pulse view. */
function SummaryLede({
  session,
  summary,
  summaryLoading,
}: {
  session: SessionDetailType;
  summary?: SessionSummaryResponse;
  summaryLoading: boolean;
}) {
  const showNarrative =
    !!summary?.narrativeGenerated &&
    !!summary?.narrative &&
    summary.narrative.trim() !== (summary.executiveSummary ?? '').trim();
  const hasPRs = (session.prLinks?.length ?? 0) > 0;

  if (!summaryLoading && !summary?.executiveSummary && !showNarrative && !hasPRs) return null;

  return (
    <Card radius="card">
      <Card.Body className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <EyebrowLabel dot color="orange">AI Summary</EyebrowLabel>
          {summary?.executiveSummary && <CopyButton value={summary.executiveSummary} label="Copy summary" />}
        </div>

        {summaryLoading ? (
          <SkeletonText lines={3} />
        ) : (
          <>
            {summary?.executiveSummary && (
              <p className={cx('text-base leading-relaxed', text.primary)}>{summary.executiveSummary}</p>
            )}
            {showNarrative && (
              <div className={cx('prose prose-sm max-w-none dark:prose-invert whitespace-pre-line', text.secondary)}>
                {summary.narrative}
              </div>
            )}
          </>
        )}

        {hasPRs && (
          <div className="pt-1">
            <EyebrowLabel color="muted">Pull requests</EyebrowLabel>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {session.prLinks!.map((link) => (
                <a key={link.prUrl} href={link.prUrl} target="_blank" rel="noreferrer noopener" title={`${link.prRepository}#${link.prNumber}`}>
                  <Badge variant="info" icon={<GitPullRequest className="w-3.5 h-3.5" />}>
                    #{link.prNumber}
                  </Badge>
                </a>
              ))}
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

/** Details view: grouped metrics, tokens, active-time, tools, logs. */
function DetailsView({
  session,
  summary,
}: {
  session: SessionDetailType;
  summary?: SessionSummaryResponse;
}) {
  const t = tokenTotals(session);
  const idleMs = session.timeBreakdown ? parseInt(session.timeBreakdown.idleMs || '0', 10) : 0;
  // session_mode is empty until classified; only render a badge for a known mode.
  const mode = session.sessionMode ? SESSION_MODE_LABELS[session.sessionMode] : undefined;
  const tools = [...(session.tools ?? [])].sort(
    (a, b) => parseInt(b.totalCalls || '0', 10) - parseInt(a.totalCalls || '0', 10),
  );
  const eventCount = summary?.recentActivity?.length ?? 0;

  return (
    <div className="space-y-4">
      {/* Activity metrics (session + subagents aggregated). */}
      <SectionCard title="Metrics">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          <StatCard surface="plain" variant="compact" label="Total Turns" value={formatInt(session.totalTurns + (session.subagentTotalTurns || 0))} />
          <StatCard surface="plain" variant="compact" label="Prompts" value={formatInt(session.turnCount + (session.subagentTurnCount || 0))} />
          <StatCard surface="plain" variant="compact" label="AI Turns" value={formatInt(session.aiTurnCount + (session.subagentAiTurnCount || 0))} />
          <StatCard surface="plain" variant="compact" label="Tool Calls" value={formatInt(session.toolCallCount + (session.subagentToolCallCount || 0))} />
          <StatCard surface="plain" variant="compact" label="Files Modified" value={formatInt(session.filesModified)} />
          <StatCard surface="plain" variant="compact" label="User Words" value={formatInt((session.userWordCount || 0) + (session.subagentUserWordCount || 0))} />
          <div title={AUTONOMY_TOOLTIP} className="cursor-help">
            <StatCard surface="plain" variant="compact" label="Autonomy" value={session.autonomyScore ? `${(session.autonomyScore * 100).toFixed(0)}%` : '—'} />
          </div>
          <StatCard surface="plain" variant="compact" label="AI turns per prompt" value={session.aiTurnsPerUserTurn ? session.aiTurnsPerUserTurn.toFixed(2) : '—'} />
        </div>
      </SectionCard>

      {/* Stretch-length autonomy distribution (#362) — the replacement for the
          saturating autonomy score. A "stretch" is a run of consecutive tool
          calls between genuine prompts. Gated on a real stretch (max > 0) so a
          trivial/pre-#362 session (no tool-call stretches) hides the section
          entirely rather than rendering an all-em-dash grid that reads broken. */}
      {(session.maxStretch ?? 0) > 0 && (
        <SectionCard
          title={<span title={STRETCH_TOOLTIP} className="cursor-help">Autonomy Distribution (Stretches)</span>}
          action={mode ? <Badge variant={mode.variant} size="sm">{mode.label}</Badge> : undefined}
        >
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <StatCard surface="plain" variant="compact" label="Median Stretch (tool calls)" value={session.medianStretch != null ? formatInt(session.medianStretch) : '—'} />
            <StatCard surface="plain" variant="compact" label="P90 Stretch (tool calls)" value={session.p90Stretch != null ? formatInt(session.p90Stretch) : '—'} />
            <StatCard surface="plain" variant="compact" label="Max Stretch (tool calls)" value={formatInt(session.maxStretch ?? 0)} />
            <StatCard surface="plain" variant="compact" label="Tools / Prompt" value={session.toolsPerGenuinePrompt ? session.toolsPerGenuinePrompt.toFixed(2) : '—'} />
            <StatCard surface="plain" variant="compact" label="In Long Stretches" value={session.pctInLongStretches != null ? `${(session.pctInLongStretches * 100).toFixed(0)}%` : '—'} />
          </div>
        </SectionCard>
      )}

      {/* Token breakdown — same stat-card structure as Metrics. */}
      {t.total > 0 && (
        <SectionCard title="Token Breakdown">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            <StatCard surface="plain" variant="compact" label="Total" value={formatInt(t.total)} />
            <StatCard surface="plain" variant="compact" label="Input" value={formatInt(t.input)} />
            <StatCard surface="plain" variant="compact" label="Output" value={formatInt(t.output)} />
            {t.noCacheWrites ? (
              <div title={NO_CACHE_WRITE_TOOLTIP} className="cursor-help">
                <StatCard surface="plain" variant="compact" label="Cache Creation" value="N/A" />
              </div>
            ) : (
              <StatCard surface="plain" variant="compact" label="Cache Creation" value={formatInt(t.cacheCreation)} />
            )}
            <StatCard surface="plain" variant="compact" label="Cache Read" value={formatInt(t.cacheRead)} />
            <StatCard surface="plain" variant="compact" label="Cache Hit Rate" value={formatPercentage(t.cacheHitRatio)} />
            <StatCard surface="plain" variant="compact" label="Billed Tokens" value={formatInt(Math.round(t.cost))} />
            {(session.costUsd ?? 0) > 0 && (
              <StatCard
                surface="plain"
                variant="compact"
                label={session.costComplete ? 'Cost (USD)' : 'Cost (USD, partial)'}
                value={formatUsd(session.costUsd ?? 0)}
              />
            )}
          </div>
          <p className={cx('text-xs', text.muted)} title={`${TOTAL_TOKENS_TOOLTIP} ${COST_TOKENS_TOOLTIP}`}>
            Billed = cost-weighted tokens (output 5×, cache-create 1.25×, cache-read 0.1×).
          </p>
        </SectionCard>
      )}

      {/* Active time. */}
      {session.timeBreakdown && (
        <SectionCard
          title="Active Time"
          action={idleMs > 0 ? <span className={cx('text-xs font-mono tabular-nums', text.muted)}>{formatDuration(idleMs)} idle</span> : undefined}
        >
          <TimeRibbon breakdown={session.timeBreakdown} height="h-4" showLegend excludeIdle />
        </SectionCard>
      )}

      {/* Tool usage — stat-card grid. */}
      {tools.length > 0 && (
        <SectionCard title="Tool Usage">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {tools.map((tool) => (
              <StatCard surface="plain" key={tool.name} variant="compact" label={tool.name} value={formatInt(parseInt(tool.totalCalls || '0', 10))} />
            ))}
          </div>
        </SectionCard>
      )}

      {/* Event log — timestamped activity rows in its own card. */}
      {eventCount > 0 && (
        <Card radius="card">
          <Card.Body>
            <Disclosure title={<EyebrowLabel dot color="orange">Event Log ({formatInt(eventCount)})</EyebrowLabel>}>
              <EventLog events={summary!.recentActivity} />
            </Disclosure>
          </Card.Body>
        </Card>
      )}

    </div>
  );
}

type View = 'pulse' | 'chapters' | 'details';

export function SessionDetail() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: session, isLoading, error } = useQuery({
    queryKey: ['session', sessionId],
    queryFn: () => api.getSession(sessionId!),
    enabled: !!sessionId,
  });

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['session-summary', sessionId],
    queryFn: () => api.getSessionSummary(sessionId!),
    enabled: !!sessionId,
    refetchInterval: (query) => {
      const status = query.state.data?.summarizationStatus?.status;
      return status === 'pending' || status === 'processing' ? 5000 : false;
    },
  });

  const isProcessing =
    summary?.summarizationStatus?.status === 'pending' || summary?.summarizationStatus?.status === 'processing';
  const failedSummary = summary?.summarizationStatus?.status === 'failed';

  if (isLoading) {
    return (
      <PageContainer width="narrow">
        {/* Keep the tab title stable and descriptive while the session loads,
            so bookmarks/history/screen readers get a meaningful name rather
            than the stale previous page (matches the loaded-state title). The
            title must be a single string child or React 19 won't hoist it. */}
        <title>{`Dixie :: Session ${sessionId?.slice(0, 8) ?? ''}`}</title>
        <Card radius="card"><Card.Body><SkeletonText lines={3} /></Card.Body></Card>
      </PageContainer>
    );
  }
  if (error) {
    return (
      <PageContainer width="narrow">
        <title>Dixie :: Session</title>
        <Alert variant="error">Failed to load session: {errorMessage(error)}</Alert>
        <Link to="/sessions" className={cx('mt-2 inline-block', text.link)}>Back to sessions</Link>
      </PageContainer>
    );
  }
  if (!session) {
    return (
      <PageContainer width="narrow">
        <title>Dixie :: Session not found</title>
        <Alert variant="warning">Session not found</Alert>
        <Link to="/sessions" className={cx('mt-2 inline-block', text.link)}>Back to sessions</Link>
      </PageContainer>
    );
  }

  const hasChapters = (session.sittings?.length ?? 0) > 1;
  const tabs = [
    { id: 'pulse', label: 'Pulse' },
    ...(hasChapters ? [{ id: 'chapters', label: 'Chapters' }] : []),
    { id: 'details', label: 'Details' },
  ];
  const requested = (searchParams.get('view') as View) || 'pulse';
  const view: View = tabs.some((t) => t.id === requested) ? requested : 'pulse';
  const setView = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('view', id);
    setSearchParams(next, { replace: true });
  };
  // Link-through from a coaching insight to the chapter it's grounded in: switch
  // to the Chapters tab, then scroll that chapter into view once it has rendered.
  const goToChapter = (ordinal: number) => {
    setView('chapters');
    setTimeout(() => {
      document.getElementById(`sitting-${ordinal}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  };

  return (
    <PageContainer width="narrow">
      {/* Single string child so React 19 hoists it — an interpolated
          "Dixie :: Session {id}" is two children and silently renders empty. */}
      <title>{`Dixie :: Session ${session.sessionId.slice(0, 8)}`}</title>

      {/* Breadcrumb + title + meta. */}
      <div className="space-y-1">
        <Link to="/sessions" className={cx('text-sm', text.link)}>Sessions</Link>
        {/* aiTitle is absent for un-summarized sessions (and backfilled
            per-source), so the headline falls back to the short id rather than a
            generic "Session" that reads identically for every untitled session
            — same convention as the Sessions list row headline (#688). */}
        <h1 className={pageTitle} style={{ textWrap: 'pretty' }}>
          {session.aiTitle || `Session ${session.sessionId.slice(0, 8)}`}
        </h1>
        <div className={cx('flex flex-wrap items-center gap-x-4 gap-y-1 text-sm', text.secondary)}>
          <span className={cx('font-medium', text.primary)}>{session.project || 'Unknown'}</span>
          <SourceBadge source={session.source} />
          <span className="inline-flex items-center gap-1" title="Host"><Monitor className="w-3.5 h-3.5" />{session.hostname || 'Unknown'}</span>
          <span className="inline-flex items-center gap-1" title="Model"><Cpu className="w-3.5 h-3.5" />{session.model || 'Unknown'}</span>
          <span className="inline-flex items-center gap-1" title="Started"><CalendarIcon className="w-3.5 h-3.5" />Started {formatDate(session.startedAt)}</span>
          <span className="inline-flex items-center gap-1" title="Duration"><TimeIcon className="w-3.5 h-3.5" />{formatDuration(session.durationMs)}</span>
          <span className="inline-flex items-center gap-1" title="Session ID">
            <code className="font-mono text-xs">{session.sessionId.slice(0, 8)}</code>
            <CopyButton value={session.sessionId} label="Copy session ID" />
          </span>
        </div>
      </div>

      {/* Lifecycle alerts. */}
      {isProcessing && <Alert variant="info">A summary for this session is being generated…</Alert>}
      {failedSummary && <Alert variant="error">{summary?.summarizationStatus?.statusMessage || 'Summarization failed.'}</Alert>}

      {/* Sub-nav: compact pill button group under the title. */}
      <div className={cx('sticky top-0 z-10 py-2', bg.page)}>
        <TabBar variant="pill" items={tabs} activeId={view} onChange={setView} />
      </div>

      {view === 'pulse' && (
        <div className="space-y-4">
          {/* Lineage: this session is one link in a continuation chain. Shown on
              the Pulse view only, below the tab bar. */}
          {session.chain && session.chain.length > 1 && (
            <div className={cx('flex flex-wrap items-center gap-2 rounded-lg border border-indigo-100 bg-indigo-50/50 px-3 py-2 text-sm dark:border-indigo-900/30 dark:bg-indigo-900/10')}>
              <span className={cx('font-medium', text.secondary)}>⛓ Chain of {session.chain.length}:</span>
              {session.chain.map((link, i) => (
                <span key={link.sessionId} className="flex items-center gap-2">
                  {i > 0 && <span className={text.muted}>→</span>}
                  {link.isCurrent ? (
                    <span className={cx('font-medium', text.primary)} title="This session">
                      {link.title || link.sessionId.slice(0, 8)}
                    </span>
                  ) : (
                    <Link
                      to={`/sessions/${link.sessionId}`}
                      className={cx('hover:underline', text.link)}
                      title={link.title || link.sessionId}
                    >
                      {link.title ? (link.title.length > 32 ? link.title.slice(0, 32) + '…' : link.title) : link.sessionId.slice(0, 8)}
                    </Link>
                  )}
                </span>
              ))}
            </div>
          )}
          {/* AI Summary → Pulse → Friction → Coaching. Friction ("how it went")
              and coaching ("how efficiently it ran") are the epilogue, read after
              the summary and activity. Multi-chapter sessions get the compact
              coaching roll-up that links through to each insight's chapter;
              single-chapter sessions show the full insight inline. */}
          <SummaryLede session={session} summary={summary} summaryLoading={summaryLoading} />
          <SessionPulseCard session={session} sittings={session.sittings ?? []} />
          <SessionFriction friction={summary?.friction} />
          {hasChapters ? (
            <CoachingRollup insights={summary?.coachingInsights} onChapter={goToChapter} />
          ) : (
            <CoachingInsights insights={summary?.coachingInsights} />
          )}
        </div>
      )}

      {view === 'chapters' && (
        <SessionChapters
          sittings={session.sittings ?? []}
          insights={summary?.coachingInsights}
          friction={summary?.friction}
        />
      )}

      {view === 'details' && <DetailsView session={session} summary={summary} />}
    </PageContainer>
  );
}
